import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, MapPin, CalendarClock, ChevronLeft, Route } from "lucide-react";
import { COMPANY, LOGO } from "@/lib/constants";
import { useLocationReporter } from "@/hooks/useLocationReporter";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import { PushPrompt } from "@/components/PushPrompt";
import { StatusBadge } from "@/components/StatusBadge";
import { api, fmtDate } from "@/lib/api";

const TABS = [
  { id: "active", label: "الحالية", match: (s) => s === "assigned" || s === "in_progress" },
  { id: "on_hold", label: "معلقة", match: (s) => s === "on_hold" },
  { id: "completed", label: "مكتملة", match: (s) => s === "completed" },
];

export default function AgentTasks() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("active");
  const load = useCallback(() => api.get("/inspections").then((r) => setItems(r.data)).catch(() => {}), []);
  const locStatus = useLocationReporter();

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const list = items.filter((i) => TABS.find((t) => t.id === tab).match(i.status));

  return (
    <div className="dark min-h-screen bg-ink text-slate-100 grid-bg">
      <header className="sticky top-0 z-20 bg-ink/90 backdrop-blur-md border-b border-line">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center gap-3">
          <img src={LOGO} alt={COMPANY} data-testid="agent-logo" className="h-10 w-10 rounded-full bg-white object-contain" />
          <div className="me-auto min-w-0">
            <p className="text-xs text-slate-400">مرحباً</p>
            <p className="font-heading font-bold truncate" data-testid="agent-name">{user.name}</p>
          </div>
          <NotificationBell dark loud onNew={load} />
          <button onClick={logout} aria-label="خروج" data-testid="logout-button" className="h-11 w-11 grid place-items-center rounded-lg border border-line hover:bg-panel-hover"><LogOut size={18} /></button>
        </div>
      </header>
      {locStatus !== "on" && (
        <div className="max-w-2xl mx-auto px-4 mt-3" data-testid="agent-location-warning">
          <p className="rounded-lg border border-amber-700 bg-amber-950/50 text-amber-200 text-sm p-3">
            {locStatus === "denied" ? "مشاركة الموقع مرفوضة. فعّلها من إعدادات المتصفح ليظهر موقعك للإدارة." : "جارٍ تحديد موقعك... أبقِ التطبيق مفتوحاً أثناء العمل."}
          </p>
        </div>
      )}
      {locStatus === "on" && <p className="max-w-2xl mx-auto px-4 mt-3 text-xs text-emerald-400" data-testid="agent-location-on">● موقعك يُشارك مع الإدارة أثناء فتح التطبيق</p>}
      <PushPrompt />
      <main className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between gap-3 mb-5 rise">
          <h1 className="font-heading text-3xl font-extrabold">مهام المعاينة</h1>
          <Link to="/agent/route" data-testid="agent-route-button" className="h-11 px-4 rounded-lg bg-brand-red text-white font-bold flex items-center gap-2 hover:bg-brand-red-dark"><Route size={18} />مسار اليوم</Link>
        </div>
        <div className="grid grid-cols-3 gap-1 p-1 rounded-xl bg-panel border border-line mb-5">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`agent-tab-${t.id}`}
              className={`h-11 rounded-lg text-sm font-bold transition-colors ${tab === t.id ? "bg-brand-red text-white" : "text-slate-300 hover:bg-panel-hover"}`}>
              {t.label} <span className="font-mono">({items.filter((i) => t.match(i.status)).length})</span>
            </button>
          ))}
        </div>
        {list.length === 0 && <p className="text-center text-slate-400 py-16" data-testid="agent-tasks-empty">لا توجد مهام هنا</p>}
        <div className="space-y-3" data-testid="agent-task-list">
          {list.map((it, i) => (
            <Link key={it.id} to={`/agent/inspections/${it.id}`} data-testid={`agent-task-${it.id}`} style={{ animationDelay: `${i * 50}ms` }}
              className="rise block rounded-xl bg-panel border border-line p-4 hover:border-brand-sky/70 transition-colors">
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="font-mono text-xs text-brand-sky">{it.ref}</span>
                <StatusBadge status={it.status} dark />
              </div>
              <p className="font-heading font-bold text-lg">{it.customer.name}</p>
              <div className="mt-2 space-y-1 text-sm text-slate-400">
                <p className="flex items-center gap-2"><MapPin size={14} />{[it.customer.area, it.customer.address].filter(Boolean).join(" - ") || "-"}</p>
                <p className="flex items-center gap-2"><CalendarClock size={14} />{fmtDate(it.scheduled_at)}</p>
              </div>
              <p className="mt-3 text-sm font-bold text-brand-sky flex items-center gap-1">{it.status === "completed" ? "عرض التقرير" : "فتح المعاينة"}<ChevronLeft size={16} /></p>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
